import { NavLink, useLocation } from "react-router-dom";
import { Users, LogOut, ArrowLeftRight, CalendarDays, MessageSquare, Star, Award, LayoutGrid } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import CoachAssistantBubble from "@/components/CoachAssistantBubble";

const items = [
  { to: "/coach", label: "Clients", Icon: Users, testid: "coachnav-clients", end: true },
  { to: "/coach/agenda", label: "Agenda", Icon: CalendarDays, testid: "coachnav-agenda" },
  { to: "/coach/messages", label: "Messages", Icon: MessageSquare, testid: "coachnav-messages" },
  { to: "/coach/avis", label: "Avis", Icon: Star, testid: "coachnav-reviews" },
  { to: "/coach/recompenses", label: "Récompenses", Icon: Award, testid: "coachnav-rewards" },
  { to: "/coach/admin", label: "Administration", Icon: LayoutGrid, testid: "coachnav-admin", adminOnly: true },
];

// Sous-pages regroupées sous l'onglet "Administration" (pour garder l'onglet actif dessus).
const ADMIN_PATHS = ["/coach/admin", "/coach/tarifs", "/coach/offre-bienvenue", "/coach/promos",
  "/coach/parrainage", "/coach/bibliotheque", "/coach/methode", "/coach/moteur", "/coach/moteur-mai",
  "/coach/cout-ia", "/coach/canary", "/coach/admins"];

export default function CoachLayout({ children }) {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const navItems = items.filter((it) => isAdmin || !it.adminOnly);
  const { pathname } = useLocation();
  const adminActive = ADMIN_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    const poll = () => api.get("/coach/messages/unread").then((r) => setUnread(r.data.count)).catch(() => {});
    poll();
    const iv = setInterval(poll, 8000);
    return () => clearInterval(iv);
  }, []);
  const switchRole = async () => { await api.post("/auth/switch-role"); window.location.href = "/app"; };
  return (
    <div className="App min-h-screen bg-coachbg">
      <header className="sticky top-0 z-30 bg-obsidian text-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <img src="/logo-white.png" alt="MAIRUNCOACH" className="h-7 w-auto" data-testid="brand-logo" />
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map(({ to, label, Icon, testid, end }) => (
                <NavLink key={to} to={to} end={end} data-testid={testid}
                  className={({ isActive }) => `relative flex items-center gap-2 px-3.5 py-2 text-sm rounded-sm transition-[background-color,color] ${(isActive || (testid === "coachnav-admin" && adminActive)) ? "bg-white text-obsidian" : "text-white/70 hover:text-white"}`}>
                  <Icon size={16} strokeWidth={1.6} />{label}
                  {testid === "coachnav-messages" && unread > 0 && <span data-testid="coach-messages-badge" className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-sage text-white text-[11px] font-semibold flex items-center justify-center">{unread}</span>}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button data-testid="coach-switch-client" onClick={switchRole} title="Espace client (démo)" className="p-2 rounded-sm text-white/70 hover:text-white hover:bg-white/10 transition-[background-color]"><ArrowLeftRight size={17} strokeWidth={1.6}/></button>
            )}
            <button data-testid="coach-logout" onClick={logout} className="p-2 rounded-sm text-white/70 hover:text-white hover:bg-white/10 transition-[background-color]"><LogOut size={17} strokeWidth={1.6}/></button>
          </div>
        </div>
        <nav className="lg:hidden flex overflow-x-auto no-scrollbar border-t border-white/10">
          {navItems.map(({ to, label, Icon, testid, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`${testid}-m`}
              className={({ isActive }) => `relative shrink-0 flex flex-col items-center gap-1 px-4 py-2.5 text-[11px] whitespace-nowrap ${(isActive || (testid === "coachnav-admin" && adminActive)) ? "text-white border-b-2 border-sage" : "text-white/60"}`}>
              <Icon size={17} strokeWidth={1.6} />{label}
              {testid === "coachnav-messages" && unread > 0 && <span className="absolute top-1.5 right-2 w-2 h-2 rounded-full bg-sage" />}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">{children}</main>
      <footer className="max-w-6xl mx-auto px-4 sm:px-6 pb-8 pt-2">
        <NavLink to="/cgu" data-testid="coach-cgu" className="text-xs text-obsidian/35 hover:text-obsidian/60">Conditions Générales d'Utilisation</NavLink>
      </footer>
      <CoachAssistantBubble />
    </div>
  );
}
